/**
 * What a link panel or dialog is about (spec §8.11, §8.25): a block, whose links and facts live on its
 * node in the page document, or — M24 — a page, whose links are workspace links (`s` = the page's IRI)
 * and whose facts live on its node in the workspace document's `pages` tree. Option groups exist only
 * on blocks (§7.6.1).
 */
import type { LoroDoc, TreeID } from "loro-crdt";
import { putFact, readFacts, removeFact, type FactEntry, type FactRow } from "../blocks/facts";
import { blockIri, docIri } from "../lib/iri";
import type { DocHandle } from "../sync/docManager";
import { addBlockLink, addWorkspaceLink, readLinks, readWorkspaceLinks, removeBlockLink, removeWorkspaceLink, type LinkEntry, type LinkObject } from "./model";

const KM = "https://w3id.org/kss/ns#";

/** A block type's class in `km.ttl` (spec §13.3), which the predicate picker asks about. */
export const BLOCK_CLASS: Record<string, string> = {
  group: "GroupBlock",
  section: "TextSectionBlock",
  code: "CodeBlock",
  mermaid: "MermaidBlock",
  image: "ImageBlock",
  audio: "AudioBlock",
  video: "VideoBlock",
  external_video: "ExternalVideoBlock",
  chemistry: "ChemistryBlock",
  reaction: "ReactionBlock",
  file: "FileBlock",
  checklist: "ChecklistBlock",
  webpage: "WebPageBlock",
  graph_view: "GraphViewBlock",
  mirror: "MirrorBlock",
  page_link: "PageLinkBlock",
  url: "UrlBlock",
  table: "TableBlock",
};

export interface LinkSubject {
  kind: "block" | "page";
  iri: string;
  /** the class the predicate picker asks about */
  subjectType: string;
  /** the document holding the links and facts */
  doc: LoroDoc;
  readLinks(doc: LoroDoc): LinkEntry[];
  addLink(p: string, o: LinkObject, userId: string, note?: string): void;
  removeLink(linkId: string): void;
  readFacts(doc: LoroDoc): FactRow[];
  putFact(userId: string, patch: Partial<FactEntry>, id?: string): void;
  removeFact(id: string): void;
  /** the block's own handle and node, for option groups; absent on a page */
  block?: { handle: DocHandle; treeId: TreeID };
}

export function blockSubject(handle: DocHandle, treeId: TreeID, blockType: string): LinkSubject {
  const doc = handle.doc;
  return {
    kind: "block",
    iri: blockIri(handle.crdtDocId, treeId),
    subjectType: `${KM}${BLOCK_CLASS[blockType] ?? "Block"}`,
    doc,
    readLinks: (d) => readLinks(d, handle.crdtDocId, treeId),
    addLink: (p, o, userId, note) => void addBlockLink(doc, treeId, p, o, userId, note),
    removeLink: (id) => removeBlockLink(doc, treeId, id),
    readFacts: (d) => readFacts(d, treeId),
    putFact: (userId, patch, id) => void putFact(doc, treeId, userId, patch, id),
    removeFact: (id) => removeFact(doc, treeId, id),
    block: { handle, treeId },
  };
}

/** A page as the subject (spec §8.25): `pageNode` is its node in the workspace doc's `pages` tree. */
export function pageSubject(wsHandle: DocHandle, workspaceId: string, docId: string, pageNode: TreeID | null): LinkSubject {
  const doc = wsHandle.doc;
  const iri = docIri(docId);
  return {
    kind: "page",
    iri,
    subjectType: `${KM}Document`,
    doc,
    readLinks: (d) => readWorkspaceLinks(d, workspaceId, iri),
    addLink: (p, o, userId, note) => void addWorkspaceLink(doc, workspaceId, iri, p, o, userId, note),
    removeLink: (id) => removeWorkspaceLink(doc, id),
    readFacts: (d) => (pageNode ? readFacts(d, pageNode, "pages") : []),
    putFact: (userId, patch, id) => {
      if (pageNode) putFact(doc, pageNode, userId, patch, id, "pages");
    },
    removeFact: (id) => {
      if (pageNode) removeFact(doc, pageNode, id, "pages");
    },
  };
}

/** The page's node in the workspace doc's `pages` tree, or null when it has none (yet). */
export function pageNodeOf(wsDoc: LoroDoc, docId: string): TreeID | null {
  for (const n of wsDoc.getTree("pages").getNodes()) {
    if (n.data.get("docId") === docId) return n.id;
  }
  return null;
}
